import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "./TopBar.tsx";
import { MessagingFailuresBanner } from "./MessagingFailuresBanner.tsx";
import { BottomTray } from "./BottomTray.tsx";
import { NewAppointmentModal } from "./NewAppointmentModal.tsx";
import { BlockTimeModal } from "./BlockTimeModal.tsx";
import { BlockIcon, CalendarIcon } from "./Icon.tsx";
import { CheckoutModal } from "./CheckoutModal.tsx";
import { getSettings } from "../lib/api.ts";
import { useTheme, type Accent } from "../lib/theme.tsx";

const VALID_ACCENTS: Accent[] = ["indigo", "forest", "rose", "amber"];

type QuickAction = "none" | "menu" | "appointment" | "block" | "collect";

export default function Layout() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { setAccent } = useTheme();
  const [action, setAction] = useState<QuickAction>("none");

  const openMenu = () => setAction("menu");
  const close = () => setAction("none");
  const refresh = () => window.dispatchEvent(new Event("zedule:refresh"));

  // Pages can open the quick-action menu by dispatching this event — no need
  // to thread the callback through Outlet context.
  useEffect(() => {
    const h = () => setAction("menu");
    window.addEventListener("zedule:new-appointment", h);
    return () => window.removeEventListener("zedule:new-appointment", h);
  }, []);

  // Consume a ?go= shortcut intent captured in sessionStorage before the redirect.
  useEffect(() => {
    if (!slug) return;
    const go = sessionStorage.getItem("zedule-go");
    if (!go) return;
    sessionStorage.removeItem("zedule-go");
    if (go === "calendar") navigate(`/${slug}/calendar`);
    else if (go === "new") setAction("appointment");
    else if (go === "collect") setAction("collect");
    // "today" → already on the tenant landing; no-op
  }, [slug, navigate]);

  // On entering a tenant, sync the dashboard accent from `business_profile.accent`.
  // localStorage already painted the cached accent at boot — this overrides it
  // when the server has a different value (e.g. owner picked rose on another device).
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getSettings(slug)
      .then((data) => {
        if (cancelled) return;
        const a = (data as { accent?: string }).accent;
        if (a && (VALID_ACCENTS as string[]).includes(a)) {
          setAccent(a as Accent);
        }
      })
      .catch(() => { /* tolerate fetch failure — keep cached accent */ });
    return () => { cancelled = true; };
  }, [slug, setAccent]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--canvas)" }}>
      <TopBar onNewAppointment={openMenu} />
      <main className="flex-1 w-full px-4 md:px-5 py-10 pb-24 md:pb-16">
        {slug && <MessagingFailuresBanner slug={slug} />}
        <Outlet />
      </main>
      <BottomTray onNewAppointment={openMenu} />

      {action === "menu" && (
        <QuickActionMenu
          onPickAppointment={() => setAction("appointment")}
          onPickBlock={() => setAction("block")}
          onPickCollect={() => setAction("collect")}
          onClose={close}
        />
      )}

      {action === "appointment" && slug && (
        <NewAppointmentModal
          slug={slug}
          onClose={close}
          onCreated={refresh}
        />
      )}

      {action === "block" && slug && (
        <BlockTimeModal
          slug={slug}
          onClose={close}
          onCreated={refresh}
        />
      )}

      {action === "collect" && slug && (
        <CheckoutModal
          slug={slug}
          standalone
          onClose={close}
          onDone={refresh}
        />
      )}
    </div>
  );
}

function QuickActionMenu({
  onPickAppointment,
  onPickBlock,
  onPickCollect,
  onClose,
}: {
  onPickAppointment: () => void;
  onPickBlock: () => void;
  onPickCollect: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={onClose}
      />
      <div
        className="relative w-full md:max-w-sm rounded-t-2xl md:rounded-2xl pb-4"
        style={{
          background: "var(--paper-raised)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div className="flex justify-center pt-3 pb-3 md:hidden">
          <div
            className="w-10 h-1 rounded-full"
            style={{ background: "var(--rule-strong)" }}
          />
        </div>
        <div className="px-2 md:py-2">
          <button
            onClick={onPickAppointment}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left transition-colors"
            style={{ color: "var(--ink)" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-well)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <span
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}
            >
              <CalendarIcon size={18} />
            </span>
            <div>
              <div className="text-sm font-medium">New appointment</div>
              <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                Manual or walk-in booking
              </div>
            </div>
          </button>
          <button
            onClick={onPickBlock}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left transition-colors"
            style={{ color: "var(--ink)" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-well)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <span
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{ background: "var(--paper-well)", color: "var(--ink-soft)" }}
            >
              <BlockIcon size={18} />
            </span>
            <div>
              <div className="text-sm font-medium">Block time</div>
              <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                Vacation, break, personal time
              </div>
            </div>
          </button>
          <button
            onClick={onPickCollect}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left transition-colors"
            style={{ color: "var(--ink)" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-well)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <span
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{ background: "var(--accent-soft)", color: "var(--accent-ink)", fontWeight: 700 }}
            >
              $
            </span>
            <div>
              <div className="text-sm font-medium">Collect payment</div>
              <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                Walk-in or ad-hoc checkout
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
